import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { formatDate, formatDateTime } from "@/lib/dates";
import { formatPassengerName } from "@/lib/passengerName";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Trophy } from "lucide-react";
import { isBilledSeriesAccountId } from "@/lib/accountSeries";

type Campaign = {
  id: string;
  campaign_name: string;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  winners_count: number;
  is_active: boolean;
  include_topups: boolean;
  include_trips: boolean;
  created_at: string;
  updated_at: string;
};

type EntryType = "topup" | "trip";
type ContactStatus = "pending" | "contacted" | "not_contactable";

type WinnerRow = {
  id: string; // winner_id
  campaign_id: string;
  entry_type: EntryType;
  // topup entries
  ecard_id: string | null;
  topup_id: string | null;
  // trip entries -- no eCard involved at all, identified by the trip itself
  job_id: string | null;
  passenger_name: string | null;
  contact_status: ContactStatus;
  winner_picked_at: string;
  picked_by_user_id: string | null;
};

type ECard = {
  id: string;
  card_last4: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  email: string | null;
};

type Topup = {
  id: string;
  topup_date: string;
  topup_amount: number;
  bonus_amount: number;
  apply_amount: number;
  payment_method: string;
};

type AllWinnerDisplay = {
  id: string;
  campaign_id: string;
  campaign_name: string;
  entry_type: EntryType;
  contact_status: ContactStatus;
  ecard_id: string | null;
  topup_id: string | null;
  job_id: string | null;
  winner_picked_at: string;
  card_last4: string;
  customer_name: string;
  email: string;
  topup_date: string;
  topup_amount: number;
};

const isoDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(
    Number.isFinite(n) ? n : 0
  );

/** Fisher-Yates -- `Array.sort(() => Math.random() - 0.5)` is a well-known
 *  biased shuffle (some orderings come up far more often than others), which
 *  matters here now that a monthly draw can pool in hundreds of trip entries
 *  alongside topups. This gives every entry a genuinely equal chance. */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function AdminECardDrawsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedId, setSelectedId] = useState<string>("new");
  const selected = useMemo(
    () => campaigns.find((c) => c.id === selectedId) || null,
    [campaigns, selectedId]
  );

  // editor fields
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState<string>(isoDate(new Date()));
  const [endDate, setEndDate] = useState<string>(isoDate(new Date()));
  const [winnersCount, setWinnersCount] = useState<string>("1");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [includeTopups, setIncludeTopups] = useState<boolean>(true);
  const [includeTrips, setIncludeTrips] = useState<boolean>(true);

  // winners display
  const [winners, setWinners] = useState<WinnerRow[]>([]);
  const [ecardMap, setEcardMap] = useState<Record<string, ECard>>({});
  const [topupMap, setTopupMap] = useState<Record<string, Topup>>({});
  const [busy, setBusy] = useState(false);

  // Previous winners section
  const [allWinners, setAllWinners] = useState<AllWinnerDisplay[]>([]);
  const [previousWinnersOpen, setPreviousWinnersOpen] = useState(false);
  const [filterCampaignId, setFilterCampaignId] = useState<string>("all");

  async function loadCampaigns() {
    const res = await supabase
      .from("ecard_draw_campaigns")
      .select("*")
      .order("start_date", { ascending: false });

    if (res.error) {
      toast({ title: "Load campaigns failed", description: res.error.message });
      return;
    }
    setCampaigns(Array.isArray(res.data) ? (res.data as Campaign[]) : []);
  }

  async function loadAllWinners() {
    // Load all winners from all campaigns
    const winnersRes = await supabase
      .from("ecard_draw_winners")
      .select("*")
      .order("winner_picked_at", { ascending: false });

    if (winnersRes.error) {
      console.error("Failed to load all winners:", winnersRes.error);
      return;
    }

    const winnerRows = Array.isArray(winnersRes.data) ? winnersRes.data : [];
    
    if (winnerRows.length === 0) {
      setAllWinners([]);
      return;
    }

    // Get unique ecard and topup IDs
    const ecardIds = Array.from(new Set(winnerRows.map((w: any) => w.ecard_id).filter(Boolean)));
    const topupIds = Array.from(new Set(winnerRows.map((w: any) => w.topup_id).filter(Boolean)));

    // Fetch ecards
    let ecardsData: any[] = [];
    if (ecardIds.length > 0) {
      const ecardsRes = await supabase
        .from("ecards")
        .select("id, card_last4, customer_first_name, customer_last_name, email")
        .in("id", ecardIds);
      if (!ecardsRes.error && Array.isArray(ecardsRes.data)) {
        ecardsData = ecardsRes.data;
      }
    }

    // Fetch topups
    let topupsData: any[] = [];
    if (topupIds.length > 0) {
      const topupsRes = await supabase
        .from("ecard_topups")
        .select("id, topup_date, topup_amount")
        .in("id", topupIds);
      if (!topupsRes.error && Array.isArray(topupsRes.data)) {
        topupsData = topupsRes.data;
      }
    }

    // Fetch campaigns
    const campaignsRes = await supabase
      .from("ecard_draw_campaigns")
      .select("id, campaign_name");
    
    const campaignsMap: Record<string, string> = {};
    if (!campaignsRes.error && Array.isArray(campaignsRes.data)) {
      for (const c of campaignsRes.data) {
        campaignsMap[c.id] = c.campaign_name;
      }
    }

    // Build lookup maps
    const ecardLookup: Record<string, any> = {};
    for (const e of ecardsData) {
      ecardLookup[e.id] = e;
    }

    const topupLookup: Record<string, any> = {};
    for (const t of topupsData) {
      topupLookup[t.id] = t;
    }

    // Build display data
    const displayData: AllWinnerDisplay[] = winnerRows.map((w: any) => {
      const ecard = ecardLookup[w.ecard_id] || {};
      const topup = topupLookup[w.topup_id] || {};
      const customerName =
        w.entry_type === "trip"
          ? w.passenger_name || "—"
          : `${ecard.customer_first_name || ""} ${ecard.customer_last_name || ""}`.trim() || "—";

      return {
        id: w.id,
        campaign_id: w.campaign_id,
        campaign_name: campaignsMap[w.campaign_id] || "Unknown Campaign",
        entry_type: (w.entry_type || "topup") as EntryType,
        contact_status: (w.contact_status || "pending") as ContactStatus,
        ecard_id: w.ecard_id ?? null,
        topup_id: w.topup_id ?? null,
        job_id: w.job_id ?? null,
        winner_picked_at: w.winner_picked_at,
        card_last4: ecard.card_last4 || "????",
        customer_name: customerName,
        email: ecard.email || "—",
        topup_date: topup.topup_date || "",
        topup_amount: topup.topup_amount || 0,
      };
    });

    setAllWinners(displayData);
  }

  async function loadWinners(campaignId: string) {
    const res = await supabase
      .from("ecard_draw_winners")
      .select("*")
      .eq("campaign_id", campaignId)
      .order("winner_picked_at", { ascending: false });

    if (res.error) {
      toast({ title: "Load winners failed", description: res.error.message });
      return;
    }

    const rows = Array.isArray(res.data) ? (res.data as any[]) : [];
    const mapped: WinnerRow[] = rows.map((r) => ({
      id: r.id,
      campaign_id: r.campaign_id,
      entry_type: (r.entry_type || "topup") as EntryType,
      ecard_id: r.ecard_id ?? null,
      topup_id: r.topup_id ?? null,
      job_id: r.job_id ?? null,
      passenger_name: r.passenger_name ?? null,
      contact_status: (r.contact_status || "pending") as ContactStatus,
      winner_picked_at: r.winner_picked_at,
      picked_by_user_id: r.picked_by_user_id ?? null,
    }));

    setWinners(mapped);

    // Fetch related cards + topups for display (batched) -- trip entries have
    // neither, so this naturally comes up empty for them.
    const ecardIds = Array.from(new Set(mapped.map((w) => w.ecard_id).filter(Boolean))) as string[];
    const topupIds = Array.from(new Set(mapped.map((w) => w.topup_id).filter(Boolean))) as string[];

    if (ecardIds.length > 0) {
      const ec = await supabase
        .from("ecards")
        .select("id, card_last4, customer_first_name, customer_last_name, email")
        .in("id", ecardIds);

      if (!ec.error && Array.isArray(ec.data)) {
        const m: Record<string, ECard> = {};
        for (const row of ec.data as any[]) {
          m[row.id] = row as ECard;
        }
        setEcardMap(m);
      }
    } else {
      setEcardMap({});
    }

    if (topupIds.length > 0) {
      const tp = await supabase
        .from("ecard_topups")
        .select("id, topup_date, topup_amount, bonus_amount, apply_amount, payment_method")
        .in("id", topupIds);

      if (!tp.error && Array.isArray(tp.data)) {
        const m: Record<string, Topup> = {};
        for (const row of tp.data as any[]) {
          m[row.id] = row as Topup;
        }
        setTopupMap(m);
      }
    } else {
      setTopupMap({});
    }
  }

  useEffect(() => {
    void loadCampaigns();
    void loadAllWinners();
  }, []);

  // hydrate editor fields
  useEffect(() => {
    if (selectedId === "new") {
      setName("");
      setStartDate(isoDate(new Date()));
      setEndDate(isoDate(new Date()));
      setWinnersCount("1");
      setIsActive(true);
      setIncludeTopups(true);
      setIncludeTrips(true);
      setWinners([]);
      setEcardMap({});
      setTopupMap({});
      return;
    }

    if (selected) {
      setName(selected.campaign_name);
      setStartDate(selected.start_date);
      setEndDate(selected.end_date);
      setWinnersCount(String(selected.winners_count ?? 1));
      setIsActive(!!selected.is_active);
      setIncludeTopups(selected.include_topups ?? true);
      setIncludeTrips(selected.include_trips ?? false);
      void loadWinners(selected.id);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  function validate(): { ok: boolean; msg?: string } {
    if (!name.trim()) return { ok: false, msg: "Campaign name is required" };
    if (!startDate || !endDate) return { ok: false, msg: "Start and end dates are required" };
    if (endDate < startDate) return { ok: false, msg: "End date must be on/after start date" };

    const wc = Number(winnersCount);
    if (!Number.isFinite(wc) || wc < 1) return { ok: false, msg: "Winners count must be at least 1" };

    if (!includeTopups && !includeTrips) {
      return { ok: false, msg: "Pick at least one entry source (Top-ups or Trips)" };
    }

    return { ok: true };
  }

  async function handleSave() {
    const v = validate();
    if (!v.ok) {
      toast({ title: "Validation error", description: v.msg });
      return;
    }

    setBusy(true);

    const payload = {
      campaign_name: name.trim(),
      start_date: startDate,
      end_date: endDate,
      winners_count: Number(winnersCount),
      is_active: isActive,
      include_topups: includeTopups,
      include_trips: includeTrips,
    };

    if (selectedId === "new") {
      const res = await supabase.from("ecard_draw_campaigns").insert([payload]).select();
      if (res.error) {
        toast({ title: "Create failed", description: res.error.message });
      } else {
        toast({ title: "Campaign created" });
        await loadCampaigns();
        if (res.data && res.data[0]) {
          setSelectedId(res.data[0].id);
        }
      }
    } else {
      const res = await supabase
        .from("ecard_draw_campaigns")
        .update(payload)
        .eq("id", selectedId);
      if (res.error) {
        toast({ title: "Update failed", description: res.error.message });
      } else {
        toast({ title: "Campaign updated" });
        await loadCampaigns();
      }
    }

    setBusy(false);
  }

  async function handleDelete() {
    if (selectedId === "new" || !selected) return;
    if (!window.confirm(`Delete campaign "${selected.campaign_name}"?`)) return;

    setBusy(true);
    const res = await supabase.from("ecard_draw_campaigns").delete().eq("id", selectedId);
    if (res.error) {
      toast({ title: "Delete failed", description: res.error.message });
    } else {
      toast({ title: "Campaign deleted" });
      setSelectedId("new");
      await loadCampaigns();
      await loadAllWinners();
    }
    setBusy(false);
  }

  // Non-account jobs: Account_ID null/blank, or its first character isn't in
  // a billed CRM series (see src/lib/accountSeries.ts) --
  // same convention used everywhere else in the app (PaymentModal.tsx etc).
  // Excludes "unknown" passenger names too, since there'd be nothing to go
  // on if that trip won.
  async function fetchEligibleTrips(campaignStart: string, campaignEnd: string, excludeJobIds: string[]) {
    let query = supabase
      .from("taxicaller_data")
      .select('"Job_ID", "Passenger_First_Name", "Passenger_Last_Name", "Account_ID", pu_date')
      .gte("pu_date", campaignStart)
      .lte("pu_date", campaignEnd)
      .not("Passenger_First_Name", "is", null)
      .neq("Passenger_First_Name", "")
      .not("Passenger_First_Name", "ilike", "unknown");

    if (excludeJobIds.length > 0) {
      query = query.not("Job_ID", "in", `(${excludeJobIds.map((id) => `"${id}"`).join(",")})`);
    }

    const res = await query;
    if (res.error) return { rows: [] as any[], error: res.error.message };

    const rows = (Array.isArray(res.data) ? res.data : []).filter((r: any) => {
      return !isBilledSeriesAccountId(r.Account_ID);
    });
    return { rows, error: null as string | null };
  }

  async function pickWinnersFor(count: number, excludeTopupIds: string[], excludeJobIds: string[]) {
    if (!selected || count <= 0) return { inserted: 0, error: null as string | null };

    type PoolEntry =
      | { kind: "topup"; ecard_id: string; topup_id: string }
      | { kind: "trip"; job_id: string; passenger_name: string };

    const pool: PoolEntry[] = [];

    // Pool 1: eCard topups within the campaign window (one topup = one chance).
    if (selected.include_topups) {
      let topupQuery = supabase
        .from("ecard_topups")
        .select("id, ecard_id, topup_date, topup_amount")
        .gte("topup_date", selected.start_date)
        .lte("topup_date", selected.end_date + "T23:59:59");
      if (excludeTopupIds.length > 0) {
        topupQuery = topupQuery.not("id", "in", `(${excludeTopupIds.join(",")})`);
      }
      const topupRes = await topupQuery;
      if (topupRes.error) return { inserted: 0, error: topupRes.error.message };
      pool.push(
        ...((Array.isArray(topupRes.data) ? topupRes.data : []) as any[]).map(
          (t): PoolEntry => ({ kind: "topup", ecard_id: t.ecard_id, topup_id: t.id })
        )
      );
    }

    // Pool 2: every non-account trip in the window (one trip = one chance).
    if (selected.include_trips) {
      const { rows: eligibleTrips, error: tripErr } = await fetchEligibleTrips(
        selected.start_date,
        selected.end_date,
        excludeJobIds
      );
      if (tripErr) return { inserted: 0, error: tripErr };
      pool.push(
        ...eligibleTrips.map(
          (r: any): PoolEntry => ({
            kind: "trip",
            job_id: r.Job_ID,
            passenger_name: formatPassengerName(r.Passenger_First_Name, r.Passenger_Last_Name),
          })
        )
      );
    }

    if (pool.length === 0) return { inserted: 0, error: "no_eligible" };

    const shuffled = shuffle(pool);
    const picked = shuffled.slice(0, Math.min(count, shuffled.length));

    const winnerInserts = picked.map((p) =>
      p.kind === "topup"
        ? {
            campaign_id: selected.id,
            entry_type: "topup",
            ecard_id: p.ecard_id,
            topup_id: p.topup_id,
            winner_picked_at: new Date().toISOString(),
          }
        : {
            campaign_id: selected.id,
            entry_type: "trip",
            job_id: p.job_id,
            passenger_name: p.passenger_name,
            winner_picked_at: new Date().toISOString(),
          }
    );

    const insertRes = await supabase.from("ecard_draw_winners").insert(winnerInserts);
    if (insertRes.error) return { inserted: 0, error: insertRes.error.message };
    return { inserted: picked.length, error: null as string | null };
  }

  async function handlePickWinners() {
    if (!selected) return;

    const activeWinners = winners.filter((w) => w.contact_status !== "not_contactable");
    const wantCount = selected.winners_count;
    const alreadyPicked = activeWinners.length;

    if (alreadyPicked >= wantCount) {
      toast({ title: "All winners already picked", description: `${alreadyPicked} of ${wantCount} winners selected.` });
      return;
    }

    setBusy(true);
    // Every past winner (including not-contactable ones) is excluded so the
    // same topup/trip is never drawn twice for this campaign.
    const excludeTopupIds = winners.filter((w) => w.topup_id).map((w) => w.topup_id as string);
    const excludeJobIds = winners.filter((w) => w.job_id).map((w) => w.job_id as string);

    const { inserted, error } = await pickWinnersFor(wantCount - alreadyPicked, excludeTopupIds, excludeJobIds);

    if (error === "no_eligible") {
      toast({ title: "No eligible entries", description: "No qualifying topups or trips found in the campaign date range." });
    } else if (error) {
      toast({ title: "Failed to pick winners", description: error });
    } else {
      toast({ title: "Winners picked!", description: `${inserted} winner(s) selected.` });
      await loadWinners(selected.id);
      await loadAllWinners();
    }

    setBusy(false);
  }

  async function handleRemoveWinner(winnerId: string) {
    if (!window.confirm("Remove this winner?")) return;

    setBusy(true);
    const res = await supabase.from("ecard_draw_winners").delete().eq("id", winnerId);
    if (res.error) {
      toast({ title: "Remove failed", description: res.error.message });
    } else {
      toast({ title: "Winner removed" });
      if (selected) await loadWinners(selected.id);
      await loadAllWinners();
    }
    setBusy(false);
  }

  // Marks a trip winner as unreachable (kept for the record, rather than
  // deleted, so the same job never gets redrawn) and immediately draws one
  // replacement so the campaign is still working toward its target count.
  async function handleMarkNotContactable(winnerId: string) {
    if (!selected) return;
    if (!window.confirm("Mark this winner as not contactable and draw a replacement?")) return;

    setBusy(true);
    const updateRes = await supabase
      .from("ecard_draw_winners")
      .update({ contact_status: "not_contactable" })
      .eq("id", winnerId);

    if (updateRes.error) {
      toast({ title: "Failed to update", description: updateRes.error.message });
      setBusy(false);
      return;
    }

    // Refresh so the exclusion lists below include the row we just updated.
    await loadWinners(selected.id);
    const refreshedRes = await supabase
      .from("ecard_draw_winners")
      .select("*")
      .eq("campaign_id", selected.id);
    const refreshed = (Array.isArray(refreshedRes.data) ? refreshedRes.data : []) as any[];
    const excludeTopupIds = refreshed.filter((w) => w.topup_id).map((w) => w.topup_id as string);
    const excludeJobIds = refreshed.filter((w) => w.job_id).map((w) => w.job_id as string);

    const { inserted, error } = await pickWinnersFor(1, excludeTopupIds, excludeJobIds);

    if (error === "no_eligible") {
      toast({ title: "Marked not contactable", description: "No remaining eligible entries to draw a replacement from." });
    } else if (error) {
      toast({ title: "Marked not contactable, but replacement draw failed", description: error });
    } else if (inserted > 0) {
      toast({ title: "Replacement drawn", description: "Marked not contactable and picked a new winner." });
    }

    await loadWinners(selected.id);
    await loadAllWinners();
    setBusy(false);
  }

  // Filter previous winners by campaign
  const filteredAllWinners = useMemo(() => {
    if (filterCampaignId === "all") return allWinners;
    return allWinners.filter((w) => w.campaign_id === filterCampaignId);
  }, [allWinners, filterCampaignId]);

  return (
    <div className="p-4 space-y-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold">eCard Draw Campaigns</h1>

      {/* Campaign selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select campaign..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="new">+ New Campaign</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.campaign_name} ({formatDate(c.start_date)} to {formatDate(c.end_date)}) --{" "}
                  {c.include_topups && c.include_trips ? "Top-ups + Trips" : c.include_trips ? "Trips only" : "Top-ups only"}
                  {c.is_active ? " [Active]" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Editor */}
      <Card>
        <CardHeader>
          <CardTitle>{selectedId === "new" ? "Create Campaign" : "Edit Campaign"}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {includeTopups && includeTrips
              ? "Every eCard top-up in this date range is a chance, plus every non-account ride (cash/EFTPOS, not a 5/6/7/8-series account) is a chance too -- topping up an eCard just adds extra chances on top."
              : includeTrips
              ? "Every non-account ride (cash/EFTPOS, not a 5/6/7/8-series account) in this date range is a chance. eCard top-ups are excluded for this campaign."
              : "Every eCard top-up in this date range is a chance. Trips are excluded for this campaign."}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Campaign Name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. January 2026 Draw"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Winners Count</label>
              <Input
                type="number"
                min="1"
                value={winnersCount}
                onChange={(e) => setWinnersCount(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="isActive" className="text-sm font-medium">
              Active
            </label>
          </div>

          <div>
            <label className="text-sm font-medium">Draw From</label>
            <div className="flex flex-wrap gap-4 mt-1">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeTopups"
                  checked={includeTopups}
                  onChange={(e) => setIncludeTopups(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="includeTopups" className="text-sm">
                  eCard Top-ups
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="includeTrips"
                  checked={includeTrips}
                  onChange={(e) => setIncludeTrips(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="includeTrips" className="text-sm">
                  Trips (non-account rides)
                </label>
              </div>
            </div>
            {!includeTopups && !includeTrips && (
              <p className="text-xs text-destructive mt-1">Pick at least one entry source.</p>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleSave} disabled={busy}>
              {selectedId === "new" ? "Create" : "Save"}
            </Button>
            {selectedId !== "new" && (
              <Button variant="destructive" onClick={handleDelete} disabled={busy}>
                Delete
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Winners section - only show for existing campaigns */}
      {selectedId !== "new" && selected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>
                Winners ({winners.filter((w) => w.contact_status !== "not_contactable").length} / {selected.winners_count})
              </span>
              <Button
                onClick={handlePickWinners}
                disabled={busy || winners.filter((w) => w.contact_status !== "not_contactable").length >= selected.winners_count}
              >
                Pick Winners
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {winners.length === 0 ? (
              <p className="text-muted-foreground">No winners picked yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry</TableHead>
                    <TableHead>Who / Card</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Picked At</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {winners.map((w) => {
                    const card = w.ecard_id ? ecardMap[w.ecard_id] : undefined;
                    const topup = w.topup_id ? topupMap[w.topup_id] : undefined;
                    const isTrip = w.entry_type === "trip";
                    return (
                      <TableRow key={w.id} className={w.contact_status === "not_contactable" ? "opacity-50" : ""}>
                        <TableCell>
                          <Badge variant={isTrip ? "secondary" : "outline"}>{isTrip ? "Trip" : "Topup"}</Badge>
                        </TableCell>
                        <TableCell>
                          {isTrip ? (
                            w.passenger_name || "—"
                          ) : (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">****{card?.card_last4 || "????"}</Badge>
                              <span>
                                {card
                                  ? `${card.customer_first_name || ""} ${card.customer_last_name || ""}`.trim() || "—"
                                  : "—"}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {isTrip ? (
                            <span className="text-muted-foreground">Job #{w.job_id}</span>
                          ) : (
                            <span>
                              {topup?.topup_date ? formatDate(topup.topup_date) : "—"}
                              {topup ? ` • ${fmtMoney(topup.topup_amount)}` : ""}
                              {card?.email ? ` • ${card.email}` : ""}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isTrip ? (
                            <Badge
                              variant={w.contact_status === "not_contactable" ? "destructive" : "outline"}
                            >
                              {w.contact_status === "not_contactable" ? "Not contactable" : "Pending contact"}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(w.winner_picked_at)}
                        </TableCell>
                        <TableCell className="flex gap-1">
                          {isTrip && w.contact_status !== "not_contactable" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkNotContactable(w.id)}
                              disabled={busy}
                            >
                              Not Contactable &amp; Redraw
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveWinner(w.id)}
                            disabled={busy}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Previous Winners Section */}
      <Card>
        <Collapsible open={previousWinnersOpen} onOpenChange={setPreviousWinnersOpen}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
              <CardTitle className="flex items-center gap-2">
                {previousWinnersOpen ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
                <Trophy className="h-5 w-5 text-yellow-500" />
                Previous Winners ({allWinners.length})
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              {/* Filter by campaign */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Filter by Campaign:</label>
                <Select value={filterCampaignId} onValueChange={setFilterCampaignId}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="All Campaigns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Campaigns</SelectItem>
                    {campaigns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.campaign_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {filteredAllWinners.length === 0 ? (
                <p className="text-muted-foreground">No winners found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Campaign Name</TableHead>
                        <TableHead>Entry</TableHead>
                        <TableHead>Who / Card</TableHead>
                        <TableHead>Details</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Winner Picked Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAllWinners.map((w) => {
                        const isTrip = w.entry_type === "trip";
                        return (
                          <TableRow key={w.id} className="bg-yellow-500/10">
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Trophy className="h-4 w-4 text-yellow-500" />
                                {w.campaign_name}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={isTrip ? "secondary" : "outline"}>{isTrip ? "Trip" : "Topup"}</Badge>
                            </TableCell>
                            <TableCell>
                              {isTrip ? (
                                w.customer_name
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">****{w.card_last4}</Badge>
                                  <span>{w.customer_name}</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              {isTrip ? (
                                <span className="text-muted-foreground">Job #{w.job_id}</span>
                              ) : (
                                <span>
                                  {w.topup_date ? formatDate(w.topup_date) : "—"}
                                  {` • ${fmtMoney(w.topup_amount)}`}
                                  {w.email !== "—" ? ` • ${w.email}` : ""}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {isTrip ? (
                                <Badge variant={w.contact_status === "not_contactable" ? "destructive" : "outline"}>
                                  {w.contact_status === "not_contactable" ? "Not contactable" : "Pending contact"}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDateTime(w.winner_picked_at)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
}

